import type { AuthCurrentUser, AuthMenu } from '@/services/trade/auth';

export type RuntimeMenuNode = {
  menuId: string;
  parentId: string;
  key: string;
  name?: string;
  path?: string;
  orderNum: number;
  parentKeys?: string[];
  routes?: RuntimeMenuNode[];
  children?: RuntimeMenuNode[];
};

const DYNAMIC_SEGMENT_PATTERN = /:([^/]+)/;

const LEGACY_ROUTE_REDIRECTS: Record<string, string> = {
  '/biz/mail-center': '/mail/collection',
  '/biz/isf-filings': '/isf/filings',
  '/biz/audit-tasks': '/isf/audit-tasks',
  '/biz/isf-filings/audit-tasks': '/isf/audit-tasks',
  '/notify/notify-channels': '/notify/channels',
  '/notify/notify-alerts': '/notify/rules',
  '/system/system-users': '/user-center/users',
  '/system/system-roles': '/user-center/roles',
  '/system/system-menus': '/user-center/menus',
  '/system/rpa-clients': '/rpa/clients',
  '/system/rpa-client-configs': '/rpa/configs',
  '/system/rpa-runtime-events': '/rpa/runtime-events',
  '/system/ace-schedule-k-ports': '/basic-data/ace-schedule-k-ports',
  '/system/mail-integration-setting': '/settings/mail-integration-setting',
  '/ai/ai-prompts': '/ai/prompts',
};

const LEGACY_DYNAMIC_ROUTE_REDIRECTS = [
  {
    pattern: /^\/biz\/mail-center\/([^/]+)$/,
    buildPath: (match: RegExpMatchArray) => `/mail/collection/${match[1]}`,
  },
  {
    pattern: /^\/notify\/notify-channels\/([^/]+)$/,
    buildPath: (match: RegExpMatchArray) => `/notify/channels/${match[1]}`,
  },
];

export function normalizeRoutePath(path?: string) {
  if (!path) {
    return undefined;
  }

  return path.replace(/\/+$/, '') || '/';
}

export function resolveCompatibleRoutePath(path?: string) {
  const normalizedPath = normalizeRoutePath(path);
  if (!normalizedPath) {
    return undefined;
  }

  const exactRedirect = LEGACY_ROUTE_REDIRECTS[normalizedPath];
  if (exactRedirect) {
    return exactRedirect;
  }

  for (const redirect of LEGACY_DYNAMIC_ROUTE_REDIRECTS) {
    const match = normalizedPath.match(redirect.pattern);
    if (match) {
      return redirect.buildPath(match);
    }
  }

  return normalizedPath;
}

export function isDynamicRoutePath(path?: string) {
  const normalizedPath = normalizeRoutePath(path);
  return !!normalizedPath && DYNAMIC_SEGMENT_PATTERN.test(normalizedPath);
}

const sortByOrder = <T extends { orderNum?: number; menuId?: string }>(items: T[] = []) =>
  [...items].sort((left, right) => {
    const leftOrder = left.orderNum || 0;
    const rightOrder = right.orderNum || 0;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return (left.menuId || '').localeCompare(right.menuId || '');
  });

const isUserAuthRoutePath = (path?: string) => path === '/user' || !!path?.startsWith('/user/');

export function collectAllowedRoutePaths(currentUser?: AuthCurrentUser, currentMenus: AuthMenu[] = []) {
  const routeSet = new Set<string>();

  for (const path of currentUser?.menuPaths || []) {
    const normalizedPath = normalizeRoutePath(path);
    if (normalizedPath) {
      routeSet.add(normalizedPath);
    }
  }

  for (const menu of currentMenus) {
    const normalizedPath = normalizeRoutePath(menu.routePath);
    if (normalizedPath) {
      routeSet.add(normalizedPath);
    }
  }

  return [...routeSet];
}

export function collectNavigableRoutePaths(currentUser?: AuthCurrentUser, currentMenus: AuthMenu[] = []) {
  const routeSet = new Set<string>();

  for (const menu of sortByOrder(currentMenus.filter((item) => item.menuType !== 2))) {
    const normalizedPath = normalizeRoutePath(menu.routePath);
    if (normalizedPath && !isDynamicRoutePath(normalizedPath)) {
      routeSet.add(normalizedPath);
    }
  }

  for (const path of currentUser?.menuPaths || []) {
    const normalizedPath = normalizeRoutePath(path);
    if (normalizedPath && !isDynamicRoutePath(normalizedPath)) {
      routeSet.add(normalizedPath);
    }
  }

  return [...routeSet];
}

export function getFirstAccessiblePath(currentUser?: AuthCurrentUser, currentMenus: AuthMenu[] = []) {
  const [firstPath] = collectNavigableRoutePaths(currentUser, currentMenus);
  return firstPath || '/home';
}

export function isRouteVisible(item: any, allowedPaths: string[]) {
  if (item?.hideInMenu) {
    return false;
  }
  if (!item?.path || isUserAuthRoutePath(item.path)) {
    return true;
  }
  const normalizedPath = normalizeRoutePath(item.path);
  return !!normalizedPath && allowedPaths.includes(normalizedPath);
}

export function filterMenuData(menuData: any[] = [], allowedPaths: string[]): any[] {
  return (menuData || [])
    .map((item): any | null => {
      const children = item.routes || item.children;
      if (Array.isArray(children) && children.length > 0) {
        const nextChildren = filterMenuData(children, allowedPaths);
        if (nextChildren.length > 0) {
          return {
            ...item,
            routes: nextChildren,
            children: nextChildren,
          };
        }
      }
      return isRouteVisible(item, allowedPaths) ? item : null;
    })
    .filter(Boolean);
}

export function buildRuntimeMenuData(currentMenus: AuthMenu[] = []) {
  const visibleMenus = sortByOrder(
    currentMenus
      .filter((item) => item.menuType !== 2)
      .map((item) => ({
        menuId: item.menuId,
        parentId: item.parentId || '0',
        name: item.menuName,
        path: normalizeRoutePath(item.routePath),
        orderNum: item.orderNum || 0,
      })),
  );

  const childrenMap = new Map<string, RuntimeMenuNode[]>();

  visibleMenus.forEach((item) => {
    const node: RuntimeMenuNode = {
      menuId: item.menuId,
      parentId: item.parentId,
      key: item.path || `menu-${item.menuId}`,
      name: item.name,
      path: item.path,
      orderNum: item.orderNum,
    };
    const siblings = childrenMap.get(node.parentId) || [];
    siblings.push(node);
    childrenMap.set(node.parentId, siblings);
  });

  const buildNode = (node: RuntimeMenuNode, parentKeys: string[] = []): RuntimeMenuNode | null => {
    const rawChildren = sortByOrder(childrenMap.get(node.menuId) || []);
    const nextChildren = rawChildren
      .map((child) => buildNode(child, [...parentKeys, node.key]))
      .filter(Boolean) as RuntimeMenuNode[];

    const nextNode: RuntimeMenuNode = {
      ...node,
      parentKeys: parentKeys.length > 0 ? parentKeys : undefined,
    };

    if (nextChildren.length > 0) {
      return {
        ...nextNode,
        routes: nextChildren,
        children: nextChildren,
      };
    }

    if (!node.path) {
      return null;
    }

    return nextNode;
  };

  return sortByOrder(childrenMap.get('0') || [])
    .map((node) => buildNode(node))
    .filter(Boolean) as RuntimeMenuNode[];
}

const normalizeCurrentPath = (path?: string) => normalizeRoutePath(path) || '/';

const routePathToRegex = (path: string) => {
  const escapedPath = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = escapedPath.replace(/:([^/]+)/g, '[^/]+');
  return new RegExp(`^${pattern}$`);
};

const isMenuPathMatched = (menuPath: string, pathname: string) => {
  if (menuPath === pathname) {
    return true;
  }
  if (!isDynamicRoutePath(menuPath)) {
    return false;
  }
  return routePathToRegex(menuPath).test(pathname);
};

const isMenuAncestorPathMatched = (menuPath: string, pathname: string) => {
  if (isDynamicRoutePath(menuPath)) {
    return false;
  }
  if (menuPath === '/') {
    return pathname === '/';
  }
  return pathname.startsWith(`${menuPath}/`);
};

export function resolveMenuSelectedKeys(currentMenus: AuthMenu[] = [], pathname?: string) {
  const normalizedPathname = resolveCompatibleRoutePath(normalizeCurrentPath(pathname)) || '/';
  const visibleMenus = currentMenus.filter((item) => item.menuType !== 2);
  const menuMap = new Map<string, AuthMenu>();

  visibleMenus.forEach((item) => {
    menuMap.set(item.menuId, item);
  });

  let matchedMenu: AuthMenu | undefined;
  for (const menu of visibleMenus) {
    const normalizedMenuPath = normalizeRoutePath(menu.routePath);
    if (!normalizedMenuPath) {
      continue;
    }
    if (isMenuPathMatched(normalizedMenuPath, normalizedPathname)) {
      if (!matchedMenu) {
        matchedMenu = menu;
        continue;
      }
      const currentDepth = normalizedMenuPath.split('/').length;
      const matchedDepth = (normalizeRoutePath(matchedMenu.routePath) || '').split('/').length;
      if (currentDepth > matchedDepth) {
        matchedMenu = menu;
      }
    }
  }

  if (!matchedMenu) {
    for (const menu of visibleMenus) {
      const normalizedMenuPath = normalizeRoutePath(menu.routePath);
      if (!normalizedMenuPath || !isMenuAncestorPathMatched(normalizedMenuPath, normalizedPathname)) {
        continue;
      }
      if (!matchedMenu) {
        matchedMenu = menu;
        continue;
      }
      const currentDepth = normalizedMenuPath.split('/').length;
      const matchedDepth = (normalizeRoutePath(matchedMenu.routePath) || '').split('/').length;
      if (currentDepth > matchedDepth) {
        matchedMenu = menu;
      }
    }
  }

  if (!matchedMenu) {
    return [normalizedPathname];
  }

  let currentMenu: AuthMenu | undefined = matchedMenu;
  while (currentMenu) {
    const normalizedMenuPath = normalizeRoutePath(currentMenu.routePath);
    if (normalizedMenuPath && !isDynamicRoutePath(normalizedMenuPath)) {
      return [normalizedMenuPath];
    }
    currentMenu = currentMenu.parentId ? menuMap.get(currentMenu.parentId) : undefined;
  }

  return [normalizedPathname];
}

export function isPathAccessible(pathname?: string, currentMenus: AuthMenu[] = []) {
  const normalizedPathname = normalizeCurrentPath(pathname);
  const compatiblePathname = resolveCompatibleRoutePath(normalizedPathname);
  const candidatePathnames = [normalizedPathname];

  if (compatiblePathname && compatiblePathname !== normalizedPathname) {
    candidatePathnames.push(compatiblePathname);
  }

  const visibleMenus = currentMenus.filter((item) => item.menuType !== 2);

  for (const candidatePathname of candidatePathnames) {
    for (const menu of visibleMenus) {
      const normalizedMenuPath = normalizeRoutePath(menu.routePath);
      if (!normalizedMenuPath) {
        continue;
      }
      if (isMenuPathMatched(normalizedMenuPath, candidatePathname)) {
        return true;
      }
      if (isMenuAncestorPathMatched(normalizedMenuPath, candidatePathname)) {
        return true;
      }
    }
  }

  return false;
}
